import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReportModal from '../../molecules/ReportModal';

/**
 * Flag reason IDs that map to translation keys
 */
const FLAG_REASONS = [
  { id: 'FALSE_RESPONSE', translationKey: 'flag_reason_false_response' },
  { id: 'INTERACTIVE_FAILED', translationKey: 'flag_reason_interactive_failed' },
  { id: 'INAPPROPRIATE_CONTENT', translationKey: 'flag_reason_inappropriate_content' },
  { id: 'OFF_TOPIC', translationKey: 'flag_reason_off_topic' },
  { id: 'OTHER', translationKey: 'flag_reason_other' },
];

/**
 * FlagReportModal - Modal for reporting problems with bot messages
 * Allows users to select a single reason via radio buttons and add an optional comment
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onSubmit - Callback when report is submitted with { reason, comment }
 * @param {boolean} props.isSubmitting - Whether the form is currently submitting
 * @returns {JSX.Element} Rendered component
 */
function FlagReportModal({ isSubmitting = false, onClose, onSubmit, open }) {
  const { t } = useTranslation(['chatbot']);

  const options = useMemo(
    () =>
      FLAG_REASONS.map((reason) => ({
        id: reason.id,
        label: t(reason.translationKey),
      })),
    [t]
  );

  const handleSubmit = useCallback(
    (data) => {
      onSubmit({
        reason: data.selectedOptions[0],
        comment: data.comment,
      });
    },
    [onSubmit]
  );

  return (
    <ReportModal
      isSubmitting={isSubmitting}
      onClose={onClose}
      onSubmit={handleSubmit}
      open={open}
      options={options}
      selectionMode="single"
      showTextarea
      submitButtonText={t('flag_submit')}
      textareaPlaceholder={t('flag_comment_placeholder')}
      title={t('flag_modal_title')}
    />
  );
}

FlagReportModal.propTypes = {
  isSubmitting: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default FlagReportModal;
